import isEqual from 'lodash/isEqual';
import React, { useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { usePrevious, useTimeoutFn } from 'react-use';

import { authSelectors } from 'app-components/flux/auth';
import { businessSelectors } from 'app-components/flux/business';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { featureFlagsSelectors, featureFlagsSimplifi } from 'app-components/flux/feature-flags';
import { subscriptionsSelectors } from 'app-components/flux/subscriptions';
import { userInfoSelectors } from 'app-components/flux/user-info';
import { tracker } from 'app-components/utils/core';
import { setFeatureFlags } from 'utils/featureFlags/initialize';

import { dispatchSimpleNotification } from 'data/notifications/notificationsUtils';
import { featureFlagsIdentifyUser } from 'utils/featureFlags/initialize';

function hasLaunchDarklyCache(ldPrefix = 'ld:') {
  return Object.keys(localStorage).some((key) => key.startsWith(ldPrefix));
}

tracker.timeEvent(tracker.events.launchDarkly);
const LaunchDarkly = () => {
  const hasSentEvent = useRef(false);
  const ldHasDatasetTier = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'ldHasDatasetTier'));
  const userInfo = useSelector(userInfoSelectors.getUserInfo);
  const authSession = useSelector(authSelectors.getAuthSession);
  const subscriptionTrackingProps = useSelector((state) =>
    subscriptionsSelectors.getSubscriptionTrackingProps(state, authSession?.datasetId),
  );
  const openBusinessCount = useSelector((state) => businessSelectors.getOpenBusinesses(state)?.size ?? 0);
  const totalBusinessCount = useSelector((state) => businessSelectors.getBusinessList(state)?.size ?? 0);
  const businessEnabled = useSelector(entitlementsSelectors.getBusinessEnabled);
  const featureFlagsLoadPending = useSelector((state) => featureFlagsSelectors.getLoadPending(state));
  const { qcsId, datasetId, datasetPlatform, datasetCreatedByClientId, datasetCreatedAt, scope, groups } = authSession;
  const { source } = userInfo || {};

  const updateTriggers = {
    qcsId,
    datasetId,
    datasetPlatform,
    datasetCreatedByClientId,
    datasetCreatedAt,
    scope,
    groups,
    source,
    openBusinessCount,
    totalBusinessCount,
    ...subscriptionTrackingProps,
  };

  const updateTriggersPrev = usePrevious(updateTriggers);
  if (!isEqual(updateTriggersPrev, updateTriggers)) {
    featureFlagsIdentifyUser(authSession, subscriptionTrackingProps, userInfo, {
      open: openBusinessCount,
      total: totalBusinessCount,
    });
  }

  const checkForError = useCallback(() => {
    if (!ldHasDatasetTier) {
      if (businessEnabled && featureFlagsLoadPending) {
        setFeatureFlags(featureFlagsSimplifi.featureFlagsSimplifiQBPDefaults);
      }
      dispatchSimpleNotification(
        'We encountered an issue while trying to fetch your settings. Some features of the app might not work correctly. Please ensure that any browser extensions are disabled and try refreshing the page.',
        { autoHideDuration: 30000, anchorOrigin: { horizontal: 'right', vertical: 'bottom' } },
      );
    }
    if (hasSentEvent.current) {
      return;
    }
    hasSentEvent.current = true;
    tracker.track(tracker.events.launchDarkly, { connected: ldHasDatasetTier, hasLDCache: hasLaunchDarklyCache() });
  }, [ldHasDatasetTier, businessEnabled, featureFlagsLoadPending]);
  const [checkIsReady, cancelCheck, resetCheck] = useTimeoutFn(checkForError, 5000);
  useEffect(() => {
    const isReady = checkIsReady();
    if (datasetId && isReady === null) {
      // cancelled
      resetCheck();
    } else if (!datasetId && isReady === false) {
      // pending
      cancelCheck();
    }
  }, [datasetId, checkIsReady, cancelCheck, resetCheck]);

  useEffect(() => {
    if (ldHasDatasetTier) {
      if (hasSentEvent.current) {
        return;
      }
      hasSentEvent.current = true;
      tracker.track(tracker.events.launchDarkly, {
        connected: ldHasDatasetTier,
        hasLDCache: hasLaunchDarklyCache(),
      });
    }
  }, [ldHasDatasetTier]);

  return <>{ldHasDatasetTier && <div id='launch-darkly-is-ready' hidden />}</>;
};

export default LaunchDarkly;
